import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import Swal from 'sweetalert2';

export interface AppVersionInfo {
  version: string;
  buildTime: string;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  private currentVersion: string | null = null;
  private isChecking: boolean = false;
  private lastCheckTimestamp: number = 0;
  private updatePending: boolean = false;
  private pendingNewVersion: string | null = null;
  private promptOpen: boolean = false;

  // Intervalo de sondeo periódico en segundo plano (cada 10 minutos)
  private readonly CHECK_INTERVAL_MS = 10 * 60 * 1000;
  // Intervalo mínimo entre comprobaciones de navegación para no saturar la red (45 seg)
  private readonly MIN_CHECK_INTERVAL_MS = 45 * 1000;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  public init(): void {
    // 1. Limpieza de marcas previas de recuperación de ChunkLoadError si ya pasaron 30 segundos
    const chunkReloadAt = sessionStorage.getItem('chunk_reload_attempted_at');
    if (chunkReloadAt && Date.now() - parseInt(chunkReloadAt, 10) > 30000) {
      sessionStorage.removeItem('chunk_reload_attempted_at');
    }

    // 2. Obtener versión inicial del sistema en ejecución
    this.fetchVersion().then(info => {
      if (info) {
        this.currentVersion = info.version;
        console.log(`[VersionCheckService] Versión activa en cliente: ${info.version} (${info.buildTime})`);

        // Si se había recargado previamente para alcanzar esta versión, limpiamos la marca
        const lastReloaded = sessionStorage.getItem('last_reloaded_version');
        if (lastReloaded === info.version) {
          sessionStorage.removeItem('last_reloaded_version');
        }
      }
    });

    // 3. Iniciar sondeo periódico cada 10 minutos
    setInterval(() => {
      this.checkForUpdate(false);
    }, this.CHECK_INTERVAL_MS);

    // 4. Suscribirse a eventos de navegación de Angular
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.onNavigationEnd(event.urlAfterRedirects || event.url);
      });
  }

  private onNavigationEnd(targetUrl: string): void {
    // Si ya existía una actualización pendiente y el usuario entra a una pantalla no crítica
    if (this.updatePending && this.pendingNewVersion) {
      if (!this.isCriticalRoute(targetUrl)) {
        console.log('[VersionCheckService] Cambio de pantalla detectado hacia ruta segura con actualización pendiente. Aplicando recarga...');
        this.applyUpdate(this.pendingNewVersion);
        return;
      }
    }

    // Comprobar si ha transcurrido el tiempo mínimo entre chequeos de navegación
    const now = Date.now();
    if (now - this.lastCheckTimestamp >= this.MIN_CHECK_INTERVAL_MS) {
      this.checkForUpdate(true, targetUrl);
    }
  }

  public checkForUpdate(isNavigation = false, targetUrl?: string): void {
    if (this.isChecking) return;
    this.isChecking = true;

    this.fetchVersion().then(remoteInfo => {
      this.isChecking = false;
      this.lastCheckTimestamp = Date.now();

      if (!remoteInfo || !remoteInfo.version) return;

      if (!this.currentVersion) {
        this.currentVersion = remoteInfo.version;
        return;
      }

      // Si la versión en servidor difiere de la versión local en memoria
      if (remoteInfo.version !== this.currentVersion) {
        console.log(`[VersionCheckService] Nueva versión detectada en servidor: ${remoteInfo.version} (Actual: ${this.currentVersion})`);

        // Protección antibucle: si ya intentamos recargar exactamente para esta versión, no reintentar
        const lastReloaded = sessionStorage.getItem('last_reloaded_version');
        if (lastReloaded === remoteInfo.version) {
          console.warn('[VersionCheckService] La versión remota coincide con la recarga anterior. Evitando bucle de recarga.');
          this.currentVersion = remoteInfo.version;
          return;
        }

        const activeUrl = targetUrl || this.router.url;
        const isCritical = this.isCriticalRoute(activeUrl);

        if (isCritical) {
          // El usuario se encuentra en una pantalla sensible (ej: venta activa)
          this.updatePending = true;
          this.pendingNewVersion = remoteInfo.version;
          this.notifyUpdateAvailable(remoteInfo.version, true);
        } else {
          if (isNavigation) {
            console.log('[VersionCheckService] Navegación a pantalla segura. Aplicando actualización de versión...');
            this.applyUpdate(remoteInfo.version);
          } else {
            this.updatePending = true;
            this.pendingNewVersion = remoteInfo.version;
            this.notifyUpdateAvailable(remoteInfo.version, false);
          }
        }
      }
    }).catch(() => {
      this.isChecking = false;
    });
  }

  private fetchVersion(): Promise<AppVersionInfo | null> {
    const timestamp = Date.now();
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    });

    return this.http
      .get<AppVersionInfo>(`assets/version.json?t=${timestamp}`, { headers })
      .toPromise()
      .catch(err => {
        console.warn('[VersionCheckService] Consulta de assets/version.json no disponible:', err);
        return null;
      });
  }

  private isCriticalRoute(url: string): boolean {
    if (!url) return false;
    const lower = url.toLowerCase();
    return (
      lower.includes('/venta') ||
      lower.includes('/compra') ||
      lower.includes('/preparar-pedido') ||
      lower.includes('/pistoleo') ||
      lower.includes('/crear-') ||
      lower.includes('addedit')
    );
  }

  private notifyUpdateAvailable(newVersion: string, inCriticalView: boolean): void {
    if (this.promptOpen) return;
    this.promptOpen = true;

    const message = inCriticalView
      ? 'Hay una nueva versión de la Intranet disponible. Se aplicará automáticamente al salir de este módulo, o puedes actualizar ahora.'
      : 'Hay una nueva versión de la Intranet disponible con mejoras. Puedes actualizar ahora para cargar los cambios.';

    Swal.fire({
      title: 'Actualización disponible',
      text: message,
      icon: 'info',
      toast: true,
      position: 'top-end',
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonColor: '#0d6efd',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Actualizar ahora',
      cancelButtonText: 'Más tarde',
      timer: 15000,
      timerProgressBar: true
    }).then((result) => {
      this.promptOpen = false;
      if (result.isConfirmed) {
        this.applyUpdate(newVersion);
      }
    });
  }

  private applyUpdate(newVersion: string): void {
    sessionStorage.setItem('last_reloaded_version', newVersion);
    window.location.reload();
  }
}
